import { LoaderFunctionArgs, useLoaderData, useRevalidator } from 'react-router';
import { clubApi, courtApi, categoryApi } from '@repo/api-client';
import { useCurrentClub } from '@/hooks/use-current-club';
import { useState } from 'react';
import { OpenMatchModal } from '@/components/open-matches/open-match-modal';
import { OpenMatchCard } from '@/components/open-matches/open-match-card';
import { Button } from '@repo/ui/components/button';
import { PlusIcon } from 'lucide-react';
import { EmptyState } from '@repo/ui/components/empty-state';
import { useAsync } from '@repo/ui/hooks/use-async';
import { WebOpenMatch } from '@repo/shared-types';

function OpenMatchesList() {
  const { matches, courts, categories } = useLoaderData<typeof route.loader>();
  const club = useCurrentClub();
  const revalidator = useRevalidator();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMatch, setEditingMatch] = useState<WebOpenMatch | undefined>();

  const { execute: handleDelete } = useAsync(async (matchId: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este partido abierto?')) {
      return;
    }
    await clubApi.deleteOpenMatch(club.id, matchId);
    revalidator.revalidate();
  });

  const handleEdit = (match: WebOpenMatch) => {
    setEditingMatch(match);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setEditingMatch(undefined);
    setModalOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Partidos Abiertos</h1>
        <Button onClick={handleCreate}>
          <PlusIcon className="size-4" />
          Crear Partido Abierto
        </Button>
      </div>

      {matches.length === 0 ? (
        <EmptyState
          title="No hay partidos abiertos"
          description="Crea un partido abierto para que los jugadores puedan expresar interés"
          actions={
            <Button onClick={handleCreate}>
              <PlusIcon className="size-4" />
              Crear Partido Abierto
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {matches.map((match) => (
            <OpenMatchCard
              key={match.id}
              match={match}
              onEdit={() => handleEdit(match)}
              onDelete={() => handleDelete(match.id)}
            />
          ))}
        </div>
      )}

      <OpenMatchModal
        open={modalOpen}
        onOpenChange={(open) => {
          setModalOpen(open);
          if (!open) {
            setEditingMatch(undefined);
          }
        }}
        courts={courts}
        categories={categories}
        initialData={editingMatch}
      />
    </div>
  );
}

export const route = {
  path: '/club/:id/partidos-abiertos',
  element: <OpenMatchesList />,
  loader: async ({ params }: LoaderFunctionArgs) => {
    const clubId = params.id as string;

    const [matchesResponse, courtsResponse, categoriesResponse] = await Promise.all([
      clubApi.getOpenMatches(clubId).catch(() => ({ results: [], count: 0 })),
      courtApi.getAll(clubId).catch(() => ({ results: [], count: 0 })),
      categoryApi.getAll().catch(() => ({ results: [], count: 0 })),
    ]);

    return {
      matches: matchesResponse.results,
      courts: courtsResponse.results,
      categories: categoriesResponse.results || [],
    };
  },
  handle: {
    title: 'Partidos Abiertos',
    backLink: (params: { id: string }) => `/club/${params.id}`,
  },
};
